import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { useDialog } from '../contexts/DialogContext';
import './CatalogoVariantesEditor.css';

const campos = ['medidas', 'madera', 'lustre', 'tapizado', 'cantidadTapizado', 'costo'];
const estado = variante => Object.fromEntries(campos.map(campo =>
  [campo, campo === 'costo' ? Number(variante[campo]) : String(variante[campo] ?? '')]));
const comunes = ['medidas', 'madera', 'tapizado', 'cantidadTapizado'];
const partesMadera = valor => [...new Set(String(valor).split('/').map(parte => parte.trim()).filter(Boolean))];

export default function CatalogoVariantesEditor({ producto, onCancelar, onGuardado }) {
  const { showConfirm } = useDialog();
  const mostrarMedidas = producto.categoria !== 'SILLA';
  const mostrarMadera = producto.categoria !== 'SOFA';
  const mostrarTapizado = !['MUEBLES', 'SOFA'].includes(producto.categoria);
  const mostrarCantidad = producto.categoria !== 'MUEBLES';
  const iniciales = Object.fromEntries(comunes.map(campo => {
    const valores = [...new Set(producto.variantes.map(variante => estado(variante)[campo]))];
    return [campo, campo === 'madera' ? producto.madera || valores.join(' / ') : valores.length === 1 ? valores[0] : ''];
  }));
  const [datos, setDatos] = useState(iniciales);
  const [terminaciones, setTerminaciones] = useState(() => {
    const fijas = producto.categoria === 'SOFA' ? ['ECO', 'CUERO', 'TELA'] : ['SIN LUSTRE', 'NATURAL', 'COMUN'];
    const grupos = new Map(fijas.map(lustre => [lustre, { clave: lustre, lustre, costos: new Set() }]));
    for (const variante of producto.variantes) {
      const actual = estado(variante);
      if (!grupos.has(actual.lustre)) grupos.set(actual.lustre, { clave: actual.lustre, lustre: actual.lustre, costos: new Set() });
      grupos.get(actual.lustre).costos.add(actual.costo);
    }
    return [...grupos.values()].map(({ costos, ...grupo }) => ({ ...grupo, existe: costos.size > 0,
      costo: costos.size === 1 ? [...costos][0] : '', costoEditado: false }));
  });
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');
  const [referenciasExcel, setReferenciasExcel] = useState({ estado: 'cargando', referencias: [] });
  useEffect(() => {
    let vigente = true;
    setReferenciasExcel({ estado: 'cargando', referencias: [] });
    api.get(`/catalogo/${producto.catalogoId}/referencias-excel`)
      .then(respuesta => { if (vigente) setReferenciasExcel({ estado: 'listo', referencias: respuesta.referencias || [] }); })
      .catch(() => { if (vigente) setReferenciasExcel({ estado: 'error', referencias: [] }); });
    return () => { vigente = false; };
  }, [producto.catalogoId]);
  const variantesPorId = new Map(producto.variantes.map(variante => [variante._id, variante]));
  const referencias = referenciasExcel.referencias.map(referencia => ({ ...referencia,
    varianteIds: referencia.varianteIds.filter(id => variantesPorId.has(id))
  })).filter(referencia => referencia.varianteIds.length);
  const identificadas = new Set(referencias.flatMap(referencia => referencia.varianteIds)).size;
  const maderas = partesMadera(producto.variantes.map(variante => variante.madera || '').join('/'));
  const maderaNueva = partesMadera(datos.madera);
  const maderaIndividual = producto.variantes.some(variante => partesMadera(variante.madera || '').length === 1);
  const maderaInvalida = datos.madera !== iniciales.madera && maderaIndividual && maderas.length > 1 && maderaNueva.length !== maderas.length;
  const ediciones = producto.variantes.map(variante => {
    const actual = estado(variante);
    const cambios = {};
    for (const campo of comunes) {
      if (datos[campo] === iniciales[campo]) continue;
      cambios[campo] = campo === 'madera' && maderas.length > 1 && partesMadera(actual.madera).length === 1 && maderaNueva.length === maderas.length
        ? maderaNueva[maderas.indexOf(actual.madera.trim())] : datos[campo];
    }
    const terminacion = terminaciones.find(grupo => grupo.clave === actual.lustre);
    if (terminacion.costoEditado && Number(terminacion.costo) !== actual.costo) cambios.costo = Number(terminacion.costo);
    return { varianteId: variante._id, actual, cambios };
  }).filter(edicion => Object.keys(edicion.cambios).length);
  const claveDatos = variante => JSON.stringify(comunes.map(campo => estado(variante)[campo]));
  const bases = [...new Map(producto.variantes.map(variante => [claveDatos(variante), variante])).values()];
  const nuevas = terminaciones.filter(grupo => grupo.costoEditado && grupo.costo !== '').flatMap(grupo =>
    bases.filter(base => !producto.variantes.some(variante => estado(variante).lustre === grupo.clave && claveDatos(variante) === claveDatos(base)))
      .map(base => ({ baseVarianteId: base._id, actual: estado(base), lustre: grupo.clave, costo: Number(grupo.costo) })));
  const cantidadCambios = ediciones.length + nuevas.length;
  const modificarCosto = (clave, valor) => setTerminaciones(actuales => actuales.map(grupo =>
    grupo.clave === clave ? { ...grupo, costo: valor, costoEditado: true } : grupo));
  const guardar = async event => {
    event.preventDefault();
    if (guardando || !cantidadCambios) return;
    setError('');
    if (maderaInvalida) {
      setError(`Este grupo contiene ${maderas.length} maderas. Indique las nuevas maderas separadas por / para conservar sus variantes.`);
      return;
    }
    const confirmado = await showConfirm(
      `Guardar cambios en ${ediciones.length} variantes del catalogo y su stock asociado.${nuevas.length ? ` Se agregaran ${nuevas.length} variantes de las terminaciones completadas.` : ''} Los presupuestos y pedidos anteriores no se modificaran.`,
      { title: 'Editar variantes', confirmLabel: 'Guardar cambios' }
    );
    if (!confirmado) return;
    setGuardando(true);
    try {
      await api.put(`/catalogo/${producto.catalogoId}/variantes`, { variantes: ediciones, ...(nuevas.length ? { nuevas } : {}) });
      await onGuardado();
    } catch (problema) {
      setError(problema.message || 'No se pudieron guardar los cambios.');
    } finally { setGuardando(false); }
  };
  return (
    <div className="modal-overlay" onClick={() => !guardando && onCancelar()}>
      <section className="modal-content catalogo-variantes-editor" role="dialog" aria-modal="true" aria-labelledby="variantes-editor-titulo"
        onClick={event => event.stopPropagation()}>
        <div className="modal-header">
          <h2 id="variantes-editor-titulo">Editar variantes</h2>
          <button type="button" className="modal-close" aria-label="Cerrar editor" disabled={guardando} onClick={onCancelar}>×</button>
        </div>
        <form onSubmit={guardar}>
          <div className="modal-body">
            <h3 className="variantes-producto">{producto.tipo} / {producto.modelo}</h3>
            <section className="variantes-referencias" aria-label="Referencias del Excel">
              <h4>Referencia Excel</h4>
              {referenciasExcel.estado === 'cargando' && <p role="status">Consultando referencias...</p>}
              {referenciasExcel.estado === 'error' && <p role="status">No se pudieron consultar las referencias del Excel.</p>}
              {referenciasExcel.estado === 'listo' && <>
                <p>{!identificadas ? 'Sin referencia vinculada' : identificadas === producto.variantes.length
                  ? 'Vinculacion completa' : 'Vinculacion parcial'} · {identificadas}/{producto.variantes.length} variantes con referencia</p>
                {referencias.length > 0 && <dl className="variantes-referencias-lista">
                  {referencias.map(referencia => <div key={referencia.idCliente}>
                    <dt><code>{referencia.idCliente}</code></dt>
                    <dd>{[...new Set(referencia.varianteIds.map(id => variantesPorId.get(id).lustre || 'Sin definir'))].join(' / ')}</dd>
                  </div>)}
                </dl>}
              </>}
            </section>
            {error && <p className="variantes-error" role="alert">{error}</p>}
            <fieldset disabled={guardando} className="variante-editor-fila">
              <legend>Datos del producto</legend>
              {mostrarMedidas && <label>Medidas<input maxLength={200} value={datos.medidas} onChange={event => setDatos({ ...datos, medidas: event.target.value })} /></label>}
              {mostrarMadera && <label>Madera<input maxLength={200} value={datos.madera} onChange={event => setDatos({ ...datos, madera: event.target.value })} /></label>}
              {mostrarTapizado && <label>Tapizado<input maxLength={200} value={datos.tapizado} onChange={event => setDatos({ ...datos, tapizado: event.target.value })} /></label>}
              {mostrarCantidad && <label>Cantidad tela / cuero<input maxLength={200} value={datos.cantidadTapizado}
                onChange={event => setDatos({ ...datos, cantidadTapizado: event.target.value })} /></label>}
            </fieldset>
            <fieldset disabled={guardando} className="variante-editor-fila variante-editor-costos">
              <legend>Costos por terminacion</legend>
              {terminaciones.map(grupo => (
                <div className="variante-editor-terminacion" key={grupo.clave}>
                  <label>Costo {grupo.lustre || 'sin definir'}<input type="number" min="0" step="0.01" required={grupo.costo !== '' || grupo.costoEditado && grupo.existe}
                    placeholder={grupo.existe ? 'Valores distintos' : 'Sin costo'} value={grupo.costo}
                    onChange={event => modificarCosto(grupo.clave, event.target.value)} /></label>
                </div>
              ))}
            </fieldset>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn-cancelar" disabled={guardando} onClick={onCancelar}>Cancelar</button>
            <button type="submit" className="btn-guardar" disabled={guardando || !cantidadCambios}>{guardando ? 'Guardando...' : 'Guardar cambios'}</button>
          </div>
        </form>
      </section>
    </div>
  );
}